/**
 * ProfileSectionHeader + ProfileBackButton — shared chrome for the in-page
 * section views (Omat yhteystiedot, Jäsenmaksu, Jäsentodistus).
 *
 * - `ProfileBackButton` is the bare, keyboard-operable "Takaisin" control. As of
 *   item-4 r1 (C5) the back affordance is lifted to the route level (`Profile`),
 *   which renders a SINGLE back control above the route <h2> for any member
 *   sub-view (the proto's per-page back-bar). The per-section back controls were
 *   removed, so this component is the route's back control now.
 * - `ProfileSectionHeader` is the subordinate level-3 section heading for the fee
 *   and certificate views (nesting under the route's level-2 "Omat tiedot"
 *   title); it no longer carries a back control.
 *
 * No raw colour literals (FR-013) — token classes only.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T030 (shared section chrome)
 */

import type { JSX } from 'react';
import { useTranslate } from '../../i18n/useTranslate.js';
import { BackArrowIcon } from '../../shell/icons.js';

export interface ProfileBackButtonProps {
  readonly onBack: () => void;
}

export function ProfileBackButton({ onBack }: ProfileBackButtonProps): JSX.Element {
  const t = useTranslate();
  return (
    <button
      type="button"
      onClick={onBack}
      className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-muted/80"
      aria-label={t('profile.back')}
      title={t('profile.back')}
    >
      <BackArrowIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

export interface ProfileSectionHeaderProps {
  readonly title: string;
}

// Sub-view section headers stay subordinate (text-lg) under the route's single
// prominent <h2> "Omat tiedot" (heading-coherence ruling); they keep the
// semantic <h3> level so there is no competing <h1>. The back control is owned
// by the route (item-4 r1 C5), so this is the heading only.
export function ProfileSectionHeader({ title }: ProfileSectionHeaderProps): JSX.Element {
  return <h3 className="text-lg font-semibold text-foreground">{title}</h3>;
}
